import React, { useEffect, useState } from "react";
import { Loader2, ClipboardCheck, NotebookPen, Sparkles, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { getMathUnitTestOptions, startMathUnitTest } from "../lib/api";
import { AtomLoader, GENERATOR_LOADER_MESSAGES } from "./AtomLoader";

/**
 * End-of-unit Summative Test + Review Sheet generator.
 *
 * Two-step flow inside one modal:
 *   1. Ask the teacher whether to generate a summative test.
 *   2. If yes — show per-Achievement-Chart-category question counts
 *      (K&U, Thinking, Communication, Application) and question-type
 *      checkboxes. Generates BOTH the test and a companion student
 *      review sheet.
 */
export default function UnitTestGeneratorDialog({
  open,
  onClose,
  grade,
  unitNumber,
  unitName,
  miniUnitLabel,
  onGenerated,
}) {
  const [step, setStep] = useState("ask"); // "ask" | "configure" | "generating"
  const [options, setOptions] = useState(null);
  const [counts, setCounts] = useState({});
  const [types, setTypes] = useState([]);
  // Feb 10 2026 — real-time backend waypoint surfaced on the wait card.
  const [progress, setProgress] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep("ask");
    let cancelled = false;
    getMathUnitTestOptions()
      .then((data) => {
        if (cancelled) return;
        setOptions(data);
        setCounts({ ...data.default_counts });
        // Default to the most common Ontario question-type mix.
        setTypes([
          "multiple_choice",
          "short_answer",
          "long_answer",
          "two_part",
        ]);
      })
      .catch((err) => {
        console.error("Failed to load unit-test options", err);
        toast.error("Couldn't load test options. Try again in a moment.");
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const total = Object.values(counts).reduce(
    (sum, n) => sum + (parseInt(n, 10) || 0),
    0
  );

  const handleCountChange = (key, raw) => {
    const v = Math.max(0, Math.min(20, parseInt(raw, 10) || 0));
    setCounts((c) => ({ ...c, [key]: v }));
  };

  const toggleType = (key) => {
    setTypes((curr) =>
      curr.includes(key) ? curr.filter((t) => t !== key) : [...curr, key]
    );
  };

  const handleGenerate = async () => {
    if (total === 0) {
      toast.error(
        "Add at least one question to one of the four Achievement Chart categories."
      );
      return;
    }
    if (types.length === 0) {
      toast.error("Pick at least one question type.");
      return;
    }
    setStep("generating");
    setProgress("");
    try {
      const job = await startMathUnitTest({
        grade,
        unit_number: unitNumber,
        mini_unit_label: miniUnitLabel || "",
        counts,
        types,
      }, { onProgress: setProgress });
      toast.success("Summative test and review sheet generated.");
      onGenerated?.(job);
      onClose?.();
    } catch (err) {
      console.error("Unit test generation failed", err);
      const msg =
        err?.response?.data?.detail || err?.message || "Generation failed.";
      toast.error(msg);
      setStep("configure");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="unit-test-dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== "generating") onClose?.();
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="unit-test-dialog"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#1c2d3d] text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#96ded1]">
              End the unit · Stage 10
            </p>
            <h3 className="text-xl font-black tracking-tight">
              Summative Test &amp; Review Sheet
            </h3>
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={step === "generating"}
            className="text-white/70 hover:text-white disabled:opacity-50"
            aria-label="Close"
            data-testid="unit-test-dialog-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step 1 — ask */}
        {step === "ask" && (
          <div className="px-6 py-6 space-y-5">
            <p className="text-sm text-[#4a6984] leading-relaxed">
              The unit is wrapping up. Would you like the engine to generate a{" "}
              <strong>summative test</strong> covering Ontario's Achievement
              Chart categories — Knowledge &amp; Understanding, Thinking,
              Communication, and Application — plus a{" "}
              <strong>student review sheet</strong> with formulas, worked
              examples, and practice questions? Students complete the review
              sheet before the test.
            </p>
            <div className="rounded-xl bg-[#96ded1]/15 border border-[#96ded1]/40 p-4 text-xs text-[#1c2d3d]/80 space-y-1.5">
              <p className="font-bold text-[#1c2d3d]">What the engine uses</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Every WALT and question type taught across this unit</li>
                <li>The diagnostic baseline + exit-ticket mastery trend</li>
                <li>The Ontario Achievement Chart + sample tasks library</li>
                <li>The Math Visual Standard for any required diagrams</li>
              </ul>
            </div>
            <p className="text-xs text-[#4a6984]/80">
              <strong>Unit:</strong> {grade} · Unit {unitNumber}
              {unitName ? ` — ${unitName}` : ""}
              {miniUnitLabel ? ` · ${miniUnitLabel}` : ""}
            </p>
            <div className="flex flex-wrap gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onClose?.()}
                className="border-[#4a6984]/20"
                data-testid="unit-test-skip"
              >
                Skip for now
              </Button>
              <Button
                type="button"
                onClick={() => setStep("configure")}
                className="bg-[#1c2d3d] hover:bg-[#0d1620] text-white"
                data-testid="unit-test-yes-generate"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Yes, set up the test
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — configure */}
        {step === "configure" && options && (
          <div className="px-6 py-6 space-y-6">
            {/* Achievement chart counts */}
            <section>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                    Ontario Achievement Chart
                  </p>
                  <h4 className="text-base font-black text-[#1c2d3d]">
                    Questions per category
                  </h4>
                </div>
                <p className="text-xs text-[#4a6984]">
                  Total:{" "}
                  <span className="font-bold text-[#1c2d3d]">{total}</span>{" "}
                  questions
                </p>
              </div>
              <div className="space-y-2.5">
                {options.categories.map((cat) => (
                  <div
                    key={cat.key}
                    className="rounded-xl border border-[#4a6984]/15 px-3 py-2.5 flex items-center gap-3"
                    data-testid={`category-row-${cat.key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-[#1c2d3d]">
                        {cat.label}
                      </p>
                      <p className="text-[11px] text-[#4a6984]/80 leading-snug">
                        {cat.description}
                      </p>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      max="20"
                      value={counts[cat.key] ?? 0}
                      onChange={(e) =>
                        handleCountChange(cat.key, e.target.value)
                      }
                      className="w-20 text-center font-bold"
                      data-testid={`category-input-${cat.key}`}
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Question types */}
            <section>
              <div className="mb-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                  Question types
                </p>
                <h4 className="text-base font-black text-[#1c2d3d]">
                  Pick the formats students will see
                </h4>
                <p className="text-[11px] text-[#4a6984]/80 mt-0.5">
                  The engine will mirror types used across your unit and
                  reserve at least one Thinking and one Application question
                  for higher-order items.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {options.question_types.map((t) => (
                  <label
                    key={t.key}
                    className="flex items-center gap-3 rounded-xl border border-[#4a6984]/15 px-3 py-2 cursor-pointer hover:bg-[#96ded1]/15"
                    data-testid={`type-checkbox-${t.key}`}
                  >
                    <Checkbox
                      checked={types.includes(t.key)}
                      onCheckedChange={() => toggleType(t.key)}
                    />
                    <span className="text-sm text-[#1c2d3d] font-medium">
                      {t.label}
                    </span>
                  </label>
                ))}
              </div>
            </section>

            <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-[#4a6984]/10">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep("ask")}
                className="border-[#4a6984]/20"
                data-testid="unit-test-back"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={handleGenerate}
                className="bg-[#1c2d3d] hover:bg-[#0d1620] text-white"
                data-testid="unit-test-generate-now"
              >
                <ClipboardCheck className="h-4 w-4 mr-2" />
                Generate test &amp; review sheet
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — generating */}
        {step === "generating" && (
          <div className="px-6 py-8 flex flex-col items-center text-center">
            <AtomLoader
              message={progress || undefined}
              messages={GENERATOR_LOADER_MESSAGES}
              sublabel="Pulling in every WALT taught, the diagnostic baseline, the exit-ticket trend, the Achievement Chart language, and the Math Visual Standard."
              data-testid="unit-test-generating-loader"
            />
          </div>
        )}
      </div>
    </div>
  );
}
